/**
 * Analytics utility functions for PostHog
 * Centralized location for all event tracking
 */

/**
 * Check if PostHog is available
 */
const isPostHogAvailable = () => {
  return typeof window !== 'undefined' && window.posthog;
};

/**
 * Track a custom event with properties
 * 
 * @param {string} eventName - Name of the event (use snake_case)
 * @param {Object} properties - Additional properties to track
 */
export const trackEvent = (eventName, properties = {}) => {
  if (!isPostHogAvailable()) {
    console.warn('PostHog not available, event not tracked:', eventName);
    return;
  }

  try {
    window.posthog.capture(eventName, {
      ...properties,
      timestamp: new Date().toISOString(),
      page_path: window.location.pathname,
      page_url: window.location.href,
    });
  } catch (error) {
    console.error('Error tracking event:', error);
  }
};

/**
 * Identify a user (call after sign-in)
 * 
 * @param {string} userId - User's unique ID (e.g., Firebase UID)
 * @param {Object} userProperties - Additional user properties
 */
export const identifyUser = (userId, userProperties = {}) => {
  if (!isPostHogAvailable()) return;

  try {
    window.posthog.identify(userId, {
      ...userProperties,
      signed_in_at: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error identifying user:', error);
  }
};

/**
 * Reset user identification (call on sign-out)
 */
export const resetUser = () => {
  if (!isPostHogAvailable()) return;

  try {
    window.posthog.reset();
  } catch (error) {
    console.error('Error resetting user:', error);
  }
};

/**
 * Set user properties (update user profile)
 * 
 * @param {Object} properties - Properties to set on user profile
 */
export const setUserProperties = (properties) => {
  if (!isPostHogAvailable()) return;

  try {
    window.posthog.setPersonProperties(properties);
  } catch (error) {
    console.error('Error setting user properties:', error);
  }
};

/**
 * Helper function to classify errors
 */
const classifyError = (errorMessage) => {
  if (!errorMessage) return 'unknown_error';
  
  const message = errorMessage.toLowerCase();
  if (message.includes('network') || message.includes('fetch')) return 'network_error';
  if (message.includes('rate limit')) return 'rate_limit_error';
  if (message.includes('authentication') || message.includes('unauthorized')) return 'auth_error';
  if (message.includes('validation') || message.includes('required')) return 'validation_error';
  if (message.includes('timeout')) return 'timeout_error';
  return 'unknown_error';
};

// ============================================================================
// Feature Interactions
// ============================================================================

/**
 * Track feature "Try it" button click
 */
export const trackFeatureTryIt = (featureId, featureTitle, userSignedIn, location) => {
  trackEvent('feature_try_it_clicked', {
    feature_id: featureId,
    feature_title: featureTitle,
    user_signed_in: userSignedIn,
    location: location, // 'landing_page' | 'design_workspace' | 'features_display'
  });
};

/**
 * Track feature form opened
 */
export const trackFeatureFormOpened = (featureId, featureTitle, triggerSource) => {
  trackEvent('feature_form_opened', {
    feature_id: featureId,
    feature_title: featureTitle,
    trigger_source: triggerSource, // 'try_it_button' | 'generate_now_button' | 'feature_thumbnail'
  });
};

// ============================================================================
// Generation Events
// ============================================================================

/**
 * Track generation started
 */
export const trackGenerationStarted = (featureType, properties = {}) => {
  trackEvent('generation_started', {
    feature_type: featureType,
    user_signed_in: properties.user_signed_in || false,
    room_type: properties.room_type || null,
    style: properties.style || null,
    has_uploaded_image: properties.has_uploaded_image || false,
    has_example_image: properties.has_example_image || false,
    has_product_url: properties.has_product_url || false,
    generation_count: properties.generation_count || null,
  });
};

/**
 * Track generation completed
 */
export const trackGenerationCompleted = (featureType, generationTimeMs, userSignedIn) => {
  trackEvent('generation_completed', {
    feature_type: featureType,
    generation_time_ms: generationTimeMs,
    success: true,
    user_signed_in: userSignedIn,
  });
};

/**
 * Track generation failed
 */
export const trackGenerationFailed = (featureType, errorMessage, userSignedIn) => {
  trackEvent('generation_failed', {
    feature_type: featureType,
    error_message: errorMessage,
    error_type: classifyError(errorMessage),
    user_signed_in: userSignedIn,
  });
};

/**
 * Track generation regenerated
 */
export const trackGenerationRegenerated = (featureType, regenerationCount, userSignedIn) => {
  trackEvent('generation_regenerated', {
    feature_type: featureType,
    regeneration_count: regenerationCount,
    user_signed_in: userSignedIn,
  });
};

// ============================================================================
// Save & Collection Events
// ============================================================================

/**
 * Track image saved
 */
export const trackImageSaved = (featureType, userSignedIn) => {
  trackEvent('image_saved', {
    feature_type: featureType,
    save_location: 'firebase',
    user_signed_in: userSignedIn, // Should always be true for saves
  });
};

/**
 * Track save attempted by unsigned user
 */
export const trackSaveAttemptedUnsigned = (featureType, triggerSource) => {
  trackEvent('save_attempted_unsigned', {
    feature_type: featureType,
    trigger_source: triggerSource, // 'save_button' | 'heart_icon'
  });
};

/**
 * Track design viewed in collection
 */
export const trackDesignViewed = (designId, viewSource) => {
  trackEvent('design_viewed', {
    design_id: designId,
    view_source: viewSource, // 'collection_grid' | 'my_designs_page'
  });
};

// ============================================================================
// Authentication Events
// ============================================================================

/**
 * Track sign-in modal opened
 */
export const trackSignInModalOpened = (triggerSource, featureContext = null) => {
  trackEvent('sign_in_modal_opened', {
    trigger_source: triggerSource, // 'save_button' | 'try_it_button' | 'subscription_modal' | 'manual'
    feature_context: featureContext,
  });
};

/**
 * Track sign-in initiated
 */
export const trackSignInInitiated = (triggerSource) => {
  trackEvent('sign_in_initiated', {
    trigger_source: triggerSource,
    provider: 'google',
  });
};

/**
 * Track sign-in completed
 */
export const trackSignInCompleted = (userId, signInMethod) => {
  identifyUser(userId, {
    sign_in_method: signInMethod,
  });
  
  trackEvent('sign_in_completed', {
    provider: 'google',
    sign_in_method: signInMethod,
  });
};

/**
 * Track sign-in failed
 */
export const trackSignInFailed = (errorCode, errorMessage) => {
  trackEvent('sign_in_failed', {
    provider: 'google',
    error_code: errorCode,
    error_message: errorMessage,
  });
};

/**
 * Track sign-out clicked
 */
export const trackSignOutClicked = (location) => {
  trackEvent('sign_out_clicked', {
    location: location, // 'header' | 'menu' | 'settings'
  });
};

// ============================================================================
// Feature-Specific Events
// ============================================================================

/**
 * Track paint canvas opened
 */
export const trackPaintCanvasOpened = (mode, featureContext) => {
  trackEvent('paint_canvas_opened', {
    mode: mode, // 'paint' | 'floor' | 'replace'
    feature_context: featureContext,
  });
};

/**
 * Track paint mask created
 */
export const trackPaintMaskCreated = (mode, brushSize, canvasTimeSeconds) => {
  trackEvent('paint_mask_created', {
    mode: mode,
    brush_size: brushSize,
    canvas_time_seconds: canvasTimeSeconds,
  });
};

/**
 * Track style reference uploaded
 */
export const trackStyleReferenceUploaded = (uploadMethod, fileSizeKb = null) => {
  trackEvent('style_reference_uploaded', {
    upload_method: uploadMethod, // 'file' | 'url'
    file_size_kb: fileSizeKb,
  });
};

/**
 * Track building type selected
 */
export const trackBuildingTypeSelected = (buildingType, selectionMethod) => {
  trackEvent('building_type_selected', {
    building_type: buildingType,
    selection_method: selectionMethod, // 'image_card' | 'dropdown'
  });
};

/**
 * Track floor material selected
 */
export const trackFloorMaterialSelected = (floorMaterial, isCustom) => {
  trackEvent('floor_material_selected', {
    floor_material: floorMaterial,
    is_custom: isCustom,
  });
};

// ============================================================================
// Product & Sharing Events
// ============================================================================

/**
 * Track product link clicked
 */
export const trackProductLinkClicked = (productIndex, productSource, featureType) => {
  trackEvent('product_link_clicked', {
    product_index: productIndex,
    product_source: productSource, // 'amazon' | 'wayfair' | 'target' | 'other'
    feature_type: featureType,
  });
};

/**
 * Track product pin clicked
 */
export const trackProductPinClicked = (productIndex, pinPosition) => {
  trackEvent('product_pin_clicked', {
    product_index: productIndex,
    pin_position: pinPosition, // {x, y}
  });
};

/**
 * Track share clicked
 */
export const trackShareClicked = (platform, featureType) => {
  trackEvent('share_clicked', {
    platform: platform, // 'facebook' | 'instagram' | 'whatsapp' | 'link'
    feature_type: featureType,
  });
};

/**
 * Track copy link clicked
 */
export const trackCopyLinkClicked = (featureType, copyMethod) => {
  trackEvent('copy_link_clicked', {
    feature_type: featureType,
    copy_method: copyMethod, // 'button' | 'shortcut'
  });
};

// ============================================================================
// Modal & UI Interactions
// ============================================================================

/**
 * Track modal opened
 */
export const trackModalOpened = (modalType, triggerSource) => {
  trackEvent('modal_opened', {
    modal_type: modalType, // 'sign_in' | 'subscription' | 'paint_canvas' | 'camera' | 'loading' | 'generate'
    trigger_source: triggerSource,
  });
};

/**
 * Track modal closed
 */
export const trackModalClosed = (modalType, closeMethod, timeOpenSeconds = null) => {
  trackEvent('modal_closed', {
    modal_type: modalType,
    close_method: closeMethod, // 'close_button' | 'overlay_click' | 'escape_key'
    time_open_seconds: timeOpenSeconds,
  });
};

/**
 * Track subscription modal shown
 */
export const trackSubscriptionModalShown = (generationCount, featureType) => {
  trackEvent('subscription_modal_shown', {
    generation_count: generationCount,
    feature_type: featureType,
  });
};

/**
 * Track camera opened
 */
export const trackCameraOpened = (featureType, cameraType) => {
  trackEvent('camera_opened', {
    feature_type: featureType,
    camera_type: cameraType, // 'environment' | 'user'
  });
};

/**
 * Track photo captured
 */
export const trackPhotoCaptured = (featureType, imageDimensions) => {
  trackEvent('photo_captured', {
    feature_type: featureType,
    image_dimensions: imageDimensions, // {width, height}
  });
};

// ============================================================================
// Image Upload Events
// ============================================================================

/**
 * Track image uploaded
 */
export const trackImageUploaded = (uploadMethod, fileSizeKb, fileType, featureType) => {
  trackEvent('image_uploaded', {
    upload_method: uploadMethod, // 'file_input' | 'drag_drop' | 'camera'
    file_size_kb: fileSizeKb,
    file_type: fileType,
    feature_type: featureType,
  });
};

/**
 * Track example image selected
 */
export const trackExampleImageSelected = (exampleImageUrl, roomType) => {
  trackEvent('example_image_selected', {
    example_image_url: exampleImageUrl,
    room_type: roomType,
  });
};

// ============================================================================
// Form Interactions
// ============================================================================

/**
 * Track form field changed
 */
export const trackFormFieldChanged = (fieldName, fieldType, featureType) => {
  trackEvent('form_field_changed', {
    field_name: fieldName,
    field_type: fieldType, // 'select' | 'text' | 'textarea' | 'color' | 'file'
    feature_type: featureType,
  });
};

/**
 * Track style selected
 */
export const trackStyleSelected = (styleName, isCustom, featureType) => {
  trackEvent('style_selected', {
    style_name: styleName,
    is_custom: isCustom,
    feature_type: featureType,
  });
};

/**
 * Track color palette selected
 */
export const trackColorPaletteSelected = (paletteName, featureType) => {
  trackEvent('color_palette_selected', {
    palette_name: paletteName,
    feature_type: featureType,
  });
};

/**
 * Track show more styles clicked
 */
export const trackShowMoreStylesClicked = (currentVisibleCount, totalStyles) => {
  trackEvent('show_more_styles_clicked', {
    current_visible_count: currentVisibleCount,
    total_styles: totalStyles,
  });
};

// ============================================================================
// Navigation Events
// ============================================================================

/**
 * Track page viewed (can be used for custom page tracking if needed)
 */
export const trackPageViewed = (pagePath, pageTitle, userSignedIn) => {
  trackEvent('page_viewed', {
    page_path: pagePath,
    page_title: pageTitle,
    user_signed_in: userSignedIn,
  });
};

/**
 * Track my designs clicked
 */
export const trackMyDesignsClicked = (location) => {
  trackEvent('my_designs_clicked', {
    location: location, // 'header' | 'navigation'
  });
};

/**
 * Track home button clicked
 */
export const trackHomeButtonClicked = (currentPage) => {
  trackEvent('home_button_clicked', {
    location: 'header',
    current_page: currentPage,
  });
};

// ============================================================================
// Special Interactions
// ============================================================================

/**
 * Track before/after toggled (if applicable)
 */
export const trackBeforeAfterToggled = (featureId, showing) => {
  trackEvent('before_after_toggled', {
    feature_id: featureId,
    showing: showing, // 'before' | 'after'
  });
};

/**
 * Track review submitted
 */
export const trackReviewSubmitted = (reviewLength, rating = null) => {
  trackEvent('review_submitted', {
    review_length: reviewLength,
    rating: rating,
  });
};

/**
 * Track collection thumbnail clicked
 */
export const trackCollectionThumbnailClicked = (collectionIndex, hasGeneratedImages) => {
  trackEvent('collection_thumbnail_clicked', {
    collection_index: collectionIndex,
    has_generated_images: hasGeneratedImages,
  });
};

// ============================================================================
// Engagement Metrics (Optional - Advanced)
// ============================================================================

/**
 * Track scroll depth (call at scroll milestones)
 */
export const trackScrollDepth = (scrollPercentage, pagePath) => {
  trackEvent('scroll_depth', {
    scroll_percentage: scrollPercentage, // 25, 50, 75, 100
    page_path: pagePath,
  });
};

/**
 * Track time on page (call periodically or on page exit)
 */
export const trackTimeOnPage = (secondsOnPage, pagePath) => {
  trackEvent('time_on_page', {
    seconds_on_page: secondsOnPage,
    page_path: pagePath,
  });
};

